import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Sparkles, Crown, Heart } from 'lucide-react';
import { getUserSubscription, getUserOrders } from '../lib/stripe';
import { getProductByPriceId } from '../stripe-config';
import { useTranslation } from '../hooks/useTranslation';

export const Success: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscription data
        const subscription = await getUserSubscription();
        if (subscription) {
          setSubscriptionData(subscription);
        }

        // Fetch recent orders
        const orders = await getUserOrders();
        if (orders && orders.length > 0) {
          setOrderData(orders[0]); // Get the most recent order
        }
      } catch (err: any) {
        console.error('Error fetching payment data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getProductInfo = () => {
    if (subscriptionData?.price_id) {
      return getProductByPriceId(subscriptionData.price_id);
    }
    return null;
  };

  const productInfo = getProductInfo();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('success.loadingPurchaseDetails')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-100 border border-red-300 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">{t('success.errorLoadingPurchase')}</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Link
              to="/commitment"
              className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <span>{t('common.tryAgain')}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('success.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('success.subtitle')}
          </p>
        </div>

        {/* Purchase Details */}
        <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
            <h2 className="text-2xl font-bold mb-2">{t('success.purchaseConfirmed')}</h2>
            {sessionId && (
              <p className="text-purple-100 text-sm">{t('success.sessionId')}: {sessionId}</p>
            )}
          </div>

          <div className="p-8">
            {subscriptionData && productInfo ? (
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                    <Crown size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{productInfo.name}</h3>
                    <p className="text-gray-600">{productInfo.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-1">{t('success.subscriptionStatus')}</h4>
                    <p className="text-purple-700 capitalize">{subscriptionData.subscription_status}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-1">{t('success.monthlyInvestment')}</h4>
                    <p className="text-green-700">${productInfo.price}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-1">{t('success.nextBilling')}</h4>
                    <p className="text-blue-700">
                      {subscriptionData.current_period_end 
                        ? new Date(subscriptionData.current_period_end * 1000).toLocaleDateString()
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>

                {subscriptionData.payment_method_brand && subscriptionData.payment_method_last4 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{t('success.paymentMethod')}</h4>
                    <p className="text-gray-700">
                      {subscriptionData.payment_method_brand.toUpperCase()} {t('success.endingIn')} {subscriptionData.payment_method_last4}
                    </p>
                  </div>
                )}
              </div>
            ) : orderData ? (
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <CheckCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('success.oneTimePurchase')}</h3>
                    <p className="text-gray-600">{t('success.orderProcessedSuccessfully')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-1">{t('success.amountPaid')}</h4>
                    <p className="text-green-700">
                      ${(orderData.amount_total / 100).toFixed(2)} {orderData.currency.toUpperCase()}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-1">{t('success.paymentStatus')}</h4>
                    <p className="text-blue-700 capitalize">{orderData.payment_status}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkles size={48} className="text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('success.thankYou')}</h3>
                <p className="text-gray-600">{t('success.purchaseConfirmedDetailsShortly')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('success.whatHappensNext')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('success.accessUnlocked')}</h4>
              <p className="text-gray-600 text-sm">{t('success.accessUnlockedDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('success.startManifesting')}</h4>
              <p className="text-gray-600 text-sm">{t('success.startManifestingDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('success.trackProgress')}</h4>
              <p className="text-gray-600 text-sm">{t('success.trackProgressDesc')}</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Link
            to="/profile"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 via-pink-600 to-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-purple-700 hover:via-pink-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span>{t('success.getStarted')}</span>
            <ArrowRight size={20} />
          </Link>
        </div>

        {/* Support Note */}
        <div className="mt-12 text-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-purple-200 max-w-2xl mx-auto">
            <Heart size={32} className="text-pink-500 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-2">{t('success.needHelp')}</h4>
            <p className="text-gray-600 text-sm mb-4">
              {t('success.supportMessage')}
            </p>
            <Link
              to="/profile?tab=support"
              className="text-purple-600 hover:text-purple-700 font-medium text-sm"
            >
              {t('success.contactSupport')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};